<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import ListsApi from "@api/lists.api";
import TagApi from "@api/tag.api";
import List from "@models/list";
import { useValidation } from "@composables/useValidation";
import { required, maxLength, minLength } from "@services/i18n-validators";
import { ElNotification } from "element-plus";

import { useRouter, useRoute } from "vue-router";
import { useLoaderStore } from "@store/loader";
import useForm from "@composables/Form";
import cancelModal from "../../../components/cancelModal.vue";

const props = defineProps({
    list: {
        type: Object,
        default: null,
    },

    item_ids: {
        // (Create New List) Button  In (Projects , Documents, Tamers) List
        //If exists create the list and store them inside it
        type: Array,
        default: () => [],
    },

    items_type: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["create", "update"]);

const tagCatalog = ref({});

const form = reactive({
    id: null,
    name: "",
    description: "",
    tags: [],
});

const saving = ref(false);
const tagLimitError = ref("");
const MAX_TAGS = 10;

// Cancel modal state
const showCancelDialog = ref(false);
const openCloseDialog = () => {
    showCancelDialog.value = true;
};
const confirmCancel = () => {
    emit("update");
    showCancelDialog.value = false;
};
const backToDialog = () => {
    showCancelDialog.value = false;
};

const rules = reactive({
    name: { required, minLength: minLength(3), maxLength: maxLength(120) },
    description: {},
});

const { v$, $externalResults } = useValidation(rules, form);

const isUpdateForm = computed(() => !!form.id);
const submitLabel = computed(() =>
    isUpdateForm.value ? "Update List" : "Create List"
);

const tagsForSelect = computed(() =>
    Object.entries(tagCatalog.value).map(([key, value]) => {
        const parsed = Number(key);
        return {
            id: Number.isNaN(parsed) ? key : parsed,
            name: value,
        };
    })
);

function registerTagOption(tag) {
    if (!tag) {
        return;
    }
    const id = tag.id ?? tag.value ?? tag.slug ?? tag;
    if (id === undefined || id === null || id === "") {
        return;
    }
    const name = tag.name ?? tag.label ?? String(id);
    const key = String(id);
    if (tagCatalog.value[key] === name) {
        return;
    }
    tagCatalog.value = {
        ...tagCatalog.value,
        [key]: name,
    };
}

function normalizeTags(rawTags) {
    if (!rawTags) {
        return [];
    }
    if (!Array.isArray(rawTags)) {
        return [];
    }

    const normalized = [];

    rawTags.forEach((tag) => {
        if (tag === null || tag === undefined) {
            return;
        }

        if (typeof tag === "object") {
            const id = tag.id ?? tag.value ?? tag.name ?? tag.label;
            if (id !== undefined && id !== null && id !== "") {
                registerTagOption({
                    id,
                    name: tag.name ?? tag.label ?? String(id),
                });
                normalized.push(id);
            }
            return;
        }

        registerTagOption({ id: tag, name: String(tag) });
        normalized.push(tag);
    });

    return normalized;
}

function hydrateFormFromProps(list) {
    form.id = list?.id ?? null;
    form.name = list?.name ?? "";
    form.description = list?.description ?? "";
    form.tags = normalizeTags(list?.tags ?? []);
    v$.value.$reset();
    $externalResults.value = {};
    tagLimitError.value = "";
}

hydrateFormFromProps(props.list);

watch(
    () => props.list,
    (next) => {
        hydrateFormFromProps(next);
    }
);

watch(
    () => form.tags.slice(),
    (current) => {
        if (!Array.isArray(current)) {
            form.tags = [];
            return;
        }

        const cleaned = current.filter(
            (tag) => tag !== undefined && tag !== null && tag !== ""
        );

        const unique = Array.from(new Set(cleaned));

        if (unique.length > MAX_TAGS) {
            tagLimitError.value = `Maximum ${MAX_TAGS} tags allowed. Only the first ${MAX_TAGS} tags have been kept.`;
            unique.splice(MAX_TAGS);
        } else {
            tagLimitError.value = "";
        }

        if (
            unique.length !== form.tags.length ||
            unique.some((value, index) => value !== form.tags[index])
        ) {
            form.tags = unique;
        }
    }
);

async function remoteTagSearch(query = "") {
    try {
        const { data } = await TagApi.list({
            Keyword: query,
            page: 0,
            limit: 50,
        });

        const tags = Array.isArray(data?.data) ? data.data : [];
        tags.forEach((tag) =>
            registerTagOption({ id: tag.id, name: tag.name })
        );
    } catch (error) {
        // Fallback silently; tag suggestions are auxiliary
    }
}

async function createTag(tagName) {
    try {
        const response = await TagApi.store({ name: tagName });
        const created = response?.data?.data?.tag;

        if (created?.id) {
            const tagObj = { id: created.id, name: created.name };
            registerTagOption(tagObj);
            form.tags.push(created.id);
            return tagObj;
        }
        const fallback = { id: tagName, name: tagName };
        registerTagOption(fallback);
        form.tags.push(tagName);

        return fallback;
    } catch (error) {
        const errorMessage = error.response?.data?.errors.message;

        let errorTxt = '';
        if (typeof errorMessage === 'string') {
            errorTxt = errorMessage
        } else {

            errorMessage.forEach(element => {
                errorTxt += element + '\n'
            });
        }
        // Use ElNotification for better UX
        ElNotification({
            title: 'Error',
            message: errorTxt,
            type: 'error',
            position: 'bottom-right',
            duration: 5000,
        });

        throw error;
    }
}

function resetForm() {
    form.id = null;
    form.name = "";
    form.description = "";
    form.tags = [];
    $externalResults.value = {};
    tagLimitError.value = "";
    v$.value.$reset();
}

async function submit() {
    console.log("item_ids", props.item_ids);
    console.log("items_type", props.items_type);
    // return;
    const isValid = await v$.value.$validate();
    if (!isValid) {
        return;
    }

    saving.value = true;
    tagLimitError.value = "";

    const payload = {
        name: form.name,
        description: form.description,
        tags: Array.isArray(form.tags)
            ? form.tags
                  .filter(
                      (tag) => tag !== undefined && tag !== null && tag !== ""
                  )
                  .map((tag) => String(tag))
            : [],
    };
    if (props.item_ids.length && props.items_type) {
        payload.item_ids = props.item_ids;
        payload.items_type = props.items_type;
    }

    try {
        let response;
        if (isUpdateForm.value) {
            response = await ListsApi.update({ ...payload, id: form.id });
        } else {
            response = await ListsApi.store(payload);
        }

        const list = response?.data?.data?.list ?? null;
        const message =
            response?.data?.message ??
            (isUpdateForm.value
                ? "List updated successfully"
                : "List created successfully");

        if (isUpdateForm.value) {
            ElNotification({
                title: "Success",
                message,
                type: "success",
                position: "bottom-left",
            });
            emit("update", list);
            window.location.reload();
        } else {
            emit("create", list);
            resetForm();
        }
    } catch (error) {
        const response = error?.response;
        const message =
            response?.data?.message ??
            "An error occurred while saving the list.";
        $externalResults.value = response?.data?.errors ?? {};
        ElNotification({
            title: "Error",
            message,
            type: "error",
            position: "bottom-left",
        });
    } finally {
        saving.value = false;
    }
}



onMounted(() => {
    remoteTagSearch();
    console.log("itmesidss", props.item_ids, props.items_type);
});
</script>

<template>
    <div class="list-form">
        <div class="step1-container">
            <form @submit.prevent="submit" class="list-form__body">
                <div class="form-field">
                    <form-input
                        id="list-name"
                        v-model="form.name"
                        :errors="v$.name"
                        :required="true"
                        label="List Name"
                        placeholder="Give Your List a Clear, Descriptive Title So It’s Easy to Recognize Later."
                    />
                </div>

                <div class="form-field">
                    <form-tiptap-text-area
                        id="list-description"
                        v-model="form.description"
                        :errors="v$.description"
                        label="Description (Optional)"
                        :required="false"
                        placeholder="Add a Short Description to Explain the Purpose or Content of This List."
                        rows="4"
                    />
                </div>

                <div class="form-field">
                    <form-el-select
                        :model="form"
                        name="tags"
                        label="Tag(s) (Optional)"
                        label-field="name"
                        :options="tagsForSelect"
                        placeholder="Add a Relevant Tag(s) to Help Users Easily Locate Your List."
                        :multiple="true"
                        :allow-create="true"
                        :remote="true"
                        clearable
                        :errors="$externalResults.value"
                        :on-create-tag="createTag"
                        @searchServerSide="remoteTagSearch"
                        
                    />
                    <small
                        v-if="tagLimitError"
                        class="text-danger small-message"
                    >
                        {{ tagLimitError }}
                    </small>
                    <small
                        v-if="$externalResults.value?.tags"
                        class="text-danger small-message"
                    >
                        {{ $externalResults.value.tags[0] }}
                    </small>
                </div>

                <div class="d-flex align-items-center justify-content-between">
                    <button
                        type="button"
                        class="btn outlineBtn"
                        @click="openCloseDialog"
                    >
                        <span>Cancel</span>
                    </button>

                    <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="saving || v$.$invalid"
                    >
                        <span v-if="saving" class="saving-spinner"></span>
                        <span>{{ submitLabel }}</span>
                    </button>
                </div>
            </form>
        </div>

        <cancelModal
            v-model="showCancelDialog"
            :text="isUpdateForm ? 'list editing' : 'list creation'"
            @cancel="confirmCancel"
            @cancelButtonClose="backToDialog"
        />
    </div>
</template>

<style scoped>
.save-disabled {
    color: #6c757d;
    font-style: italic;
}
.main-wrapper {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 160px);
}

.wizard-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
}

.form-container {
    overflow: auto;
    flex: 1 1 auto;
}

.wizard-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background: #fff;
    padding-top: 12px;
}
</style>
